// import { isMobile } from 'react-device-detect';
import { AxiosResponse, AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios';

// import eventTracker, { LoggingAction } from '@shared/utils/event/newDataWareHose';
// import { localAESStorage } from '@shared/utils/storage';
// import { LSK } from '@shared/utils/storage/config';
// import { App } from '@shared/typings/models/app';
import { trackRecorder } from '@shared/utils/event/base/recorder';
import { HttpDependency } from '@shared/utils/http/dependency';
// import { getAppCategoryName, getAppClassify } from '@shared/hooks/useAppClassify';

import { EVENT2URL, EVENT3URL, EVENT4URL } from './types';
// import { USER_SCENE_TRACK_CONFIG } from '@service/userScene/config';
// import type { UserScene } from '@service/userScene/type';
import { registerSuccessHandle, loginSuccessHandle, imageSuccessHandle } from './AccountEvent';
import type { Application } from '@shared/typings/models/app/applicationList';
import type { IBaseResponse } from '@shared/utils/http/interface';
import { trackAppVisit } from '@/mobile/views/AppDetail/utils';
import type { SearchApi } from '@service/search/type';
import { GenerateSimilarStyleData, IGenerateSimilarStyle } from '@shared/utils/ability/base';

/**
 * [统一处理 非业务代码事件]
 * 1. 数据埋点
 * 2. 百度回传
 * 3. 谷歌回传
 * ......
 */
const nonBusinessEventTracker = (res: AxiosResponse) => {
	const { config, data, headers } = res;
	const { url = '' } = config;

	// 拦截详情页接口并处理
	interceptDetailPage(url, data);

	// 拦截首页接口获取request-id
	interceptHomePage(url, headers, data);
	// 拦截搜索 Filters 接口获取 request-id
	interceptSearchPage(url, headers, data);
	interceptRecommendApp(url, headers, data);
	interceptLikeRecommendPage(url, headers, data);

	// 处理注册行为接口
	switch (url) {
		case EVENT2URL.googleLoginSuccess:
		case EVENT2URL.phoneLoginSuccess:
		case EVENT2URL.facebookLoginSuccess:
		case EVENT2URL.touristLoginSuccess:
			// 非注册事件并且没有在当前app登录过
			if (!data?.data?.register) {
				HttpDependency.registerTrackManage();
			}
			if (!data?.data?.register) break; //非注册事件，则返回
			setTimeout(() => registerSuccessHandle(data, url), 2000); // 漏执行3%左右的任务，怀疑是注册成功后执行的任务太多，导致注册成功事件没有执行完
			break;
		case EVENT2URL.registerSuccess:
			setTimeout(() => registerSuccessHandle(data, url), 2000); // 漏执行3%左右的任务，怀疑是注册成功后执行的任务太多，导致注册成功事件没有执行完
			break;
		default:
			break;
	}

	// 处理登录行为接口
	switch (url) {
		case EVENT3URL.googleLoginSuccess:
		case EVENT3URL.phoneLoginSuccess:
		case EVENT3URL.facebookLoginSuccess:
		case EVENT3URL.touristLoginSuccess:
			if (data?.data?.register !== false) break; //未注册或非登录事件，则返回
			loginSuccessHandle(data, url);
			break;
		case EVENT3URL.loginSuccess:
			if (data?.data?.code === 110008) break; //如果未注册，直接返回
			HttpDependency.registerTrackManage();
			loginSuccessHandle(data, url);
			break;
		default:
			break;
	}

	switch (url) {
		case EVENT4URL.generateSuccess:
		case EVENT4URL.generateSuccess2:
			imageSuccessHandle(url, config);
			break;
		default:
			break;
	}
};

// const containsVideo = (appData: App.DO): boolean => {
// 	if (!appData.resource?.sampleImage) return false;
// 	const { image } = appData.resource.sampleImage;
// 	if (!image) return false;
// 	if (image.endsWith('.mp4') || image.endsWith('.gif')) {
// 		return true;
// 	}
// 	return false;
// };

/**
 * 拦截详情页接口并处理
 * @param url 请求的URL
 * @param data 请求返回的数据
 */
function interceptDetailPage(url: string, data: IBaseResponse<Application.DTO>) {
	// 提取 applicationId
	const match = url.match(/^\/api\/community\/v1\/publications\/(([a-zA-Z0-9]{4,6})|(\d+))$/);
	if (match) {
		const applicationId = match[1];
		// 检查是否为纯字母，并排除掉
		const isAlphabetOnly = /^[a-zA-Z]+$/.test(applicationId);

		if (!isAlphabetOnly) {
			// 跳过一次
			if (trackRecorder.getOneTime('disable_app_visit')) return;
			const skipTrack = trackRecorder.getOneTime(`skip_app_visit_track${applicationId}`) === 'true';
			!skipTrack && trackAppVisit(data?.data);
			// const imageId = localAESStorage.getItem(LSK.MAIN_IMAGE_ID) || '';
			// const hasVideo = containsVideo(data?.data);
			// const appid = data?.data?.resource?.shortId;
			// const app_id = data?.data?.resource?.id;
			// const hasLabel = sessionStorage.getItem(SSK.APP_DETAIL_LABEL) === 'label' || isMobile; // one time的作用
			// const indexInfo = trackRecorder.getAppVisitIndex(data?.data?.resource?.id);
			// const is_ai_video = getAppClassify(data?.data?.resource?.form).includes('videoGen');
			// eventTracker.loggingEvent(LoggingAction.APP_VISIT, {
			// 	hasVideo,
			// 	app_id,
			// 	short_id: appid,
			// 	image_id: imageId,
			// 	pub_id: data?.data?.id,
			// 	tagId: hasLabel ? undefined : 'none',
			// 	tagName: hasLabel ? undefined : 'none',
			// 	gallery_id: trackRecorder.source.galleryId, // 拿了历史值无关系，只有origin正确才会报上去
			// 	is_video_gen: is_ai_video,
			// 	ability_name: getAppCategoryName(data.data),
			// 	user_scene: USER_SCENE_TRACK_CONFIG[data.data.userScene as UserScene],
			// 	...indexInfo,
			// });
			// sessionStorage.setItem(SSK.APP_DETAIL_LABEL, 'noLabel');
			// localAESStorage.removeItem(LSK.MAIN_IMAGE_ID);
		}
	}
}

/**
 * 拦截首页接口并处理
 * @param url 请求的URL
 * @param headers 请求头
 */
function interceptHomePage(
	url: string,
	headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
	data: IBaseResponse<{ list: Application.VO[] }>,
) {
	const match = url.match(/^\/api\/community\/v1\/publications\/applications/);

	if (match) {
		const traceId = headers['x-request-id']!;
		// 注入 traceId 到结果列表中
		try {
			data?.data?.list?.forEach((item) => {
				item.resource.aiRecTraceInfo =
					item.resource.aiRecTraceInfo ?? JSON.stringify({ requestId: traceId });
			});
		} catch (error) {
			// 捕获异常，防止影响正常逻辑
			console.error('interceptHomePage error:', error);
		}
	}
}

// TODO 后续搜索完善后下掉，目前前端先补上
/**
 * 拦截搜索接口并处理
 */
function interceptSearchPage(
	url: string,
	headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
	data: IBaseResponse<SearchApi.SearchResponseData>,
) {
	const match = url.match(/^\/api\/community\/v2\/search/);

	if (match) {
		const traceId = headers['x-request-id']!;
		// 注入 traceId 到结果列表中
		try {
			data?.data.appInfo.list?.forEach((item) => {
				item.resource.aiRecTraceInfo =
					item.resource.aiRecTraceInfo ?? JSON.stringify({ requestId: traceId });
			});
		} catch (error) {
			// 捕获异常，防止影响正常逻辑
			console.error('interceptHomePage error:', error);
		}
	}
}

/**
 * 拦截推荐应用接口并处理
 */
function interceptRecommendApp(
	url: string,
	headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
	data: IBaseResponse<GenerateSimilarStyleData>,
) {
    const match = url.match(/^\/api\/resources\/v2\/images\/generate-similar-style-recommend/);

    if (match) {
        const traceId = headers['x-request-id']!;
        try {
            data?.data?.recommends?.forEach((item: IGenerateSimilarStyle) => {
                item.aiRecTraceInfo = 
                    item.aiRecTraceInfo ?? JSON.stringify({ requestId: traceId });
            });
        } catch (error) {
            console.error('interceptRecommendApp error:', error);
        }
    }
}

/**
 * 拦截喜欢推荐接口并处理
 */
function interceptLikeRecommendPage(
	url: string,
	headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
	data: IBaseResponse<{ resourceList: Application.DTO[] }>,
) {
	const match = url.match(/^\/api\/community\/v1\/publications\/like-recommends/);

	if (match) {
		const traceId = headers['x-request-id']!;
		// 注入 traceId 到结果列表中
		try {
			data?.data.resourceList?.forEach((item) => {
				item.resource.aiRecTraceInfo =
					item.resource.aiRecTraceInfo ?? JSON.stringify({ requestId: traceId });
			});
		} catch (error) {
			// 捕获异常，防止影响正常逻辑
			console.error('interceptHomePage error:', error);
		}
	}
}

// TODO 注册时机整理 - @liuhengkang
export const initNoneBusiness = () => {
	HttpDependency.inject({
		nonBusinessEventTracker(res) {
			nonBusinessEventTracker(res);
		},
	});
};
